/**
 * 数据库版本与迁移路径 —— 这是最容易在用户那里出事的部分，所以规则写得比较死。
 *
 * ## 三条不可违背的规则
 *
 * 1. **升级只往前，且必须能读懂旧数据。** 用户从 0.1 升到 0.3 时，库里躺着的是 0.1 写的
 *    参数快照、航线草稿和审计日志。迁移步骤负责把它们改写成新形状；改写不了的单条记录
 *    **原样保留**并在 `migrationIssues` 里报告，绝不删除。
 * 2. **绝不通过删库来"修复"问题。** 删库等于把用户的参数快照、飞前检查记录与审计日志
 *    一起丢掉。审计日志在事故复盘时是唯一可信的信息来源，删掉它比"打开失败"严重得多。
 *    因此迁移失败时：整个升级事务中止（库回滚到旧版本，一条数据都没动），打开操作抛出
 *    带中文原因的错误，由上层降级到内存并如实告诉用户"本次改动不会被保存"。
 * 3. **未来版本号必须被拒绝。** 用旧程序打开新数据（用户回退了版本）时，
 *    IndexedDB 会直接抛 `VersionError`；我们把它翻译成"库里有比当前程序更新的数据，
 *    请升级程序"，而不是降级解读 —— 降级解读会用旧代码把新记录写坏。
 *
 * ## 迁移里为什么不能 `await`
 *
 * IndexedDB 的升级事务在"没有待处理请求"时自动提交。一旦在迁移里 `await` 一个 Promise，
 * 控制权交回事件循环，事务会在下一个任务里提交，之后任何 `put` 都会抛 `TransactionInactiveError`。
 * 所以第 3 步的整表改写写成游标回调链（`onsuccess` → `continue()`），
 * 全靠"回调里继续发请求"把事务钉住。这也是唯一正确写法，不是风格问题。
 */

import type { PersistenceCollection } from './store';
import {
  PARAM_SNAPSHOT_PREFIX,
  PERSISTENCE_SCHEMA_VERSION,
  RECORD_FORMAT,
  isRecordLike,
  paramSnapshotChunkKey,
  readFiniteNumber,
  readOptionalString,
  readString,
} from './keys';
import { buildParamSnapshot, normalizeLegacyParamSet, type BuiltParamSnapshot } from './param-snapshots';

/** 数据库自述记录，存在 meta 集合的 `schema` 键下 */
export interface SchemaMeta {
  readonly format: number;
  readonly schemaVersion: number;
  readonly updatedAt: number;
  /** 写入方标识，例如 `HGC 0.3.0`。排查"数据是谁写的"时只有它管用 */
  readonly writtenBy?: string;
}

/** 一条没能迁移的旧记录 */
export interface MigrationIssue {
  readonly collection: PersistenceCollection;
  readonly key: string;
  /** 中文可读原因，可直接显示给用户 */
  readonly reason: string;
}

/** 迁移问题清单；持久化在 meta 集合里，刷新页面后仍然可见 */
export interface MigrationIssueReport {
  readonly format: number;
  readonly schemaVersion: number;
  readonly at: number;
  readonly issues: readonly MigrationIssue[];
}

export interface MigrationContext {
  readonly db: IDBDatabase;
  readonly tx: IDBTransaction;
  readonly now: () => number;
  /** 记录一条"改不了、但保留"的旧记录 */
  readonly report: (issue: MigrationIssue) => void;
}

export interface MigrationStep {
  /** 迁移完成后的版本号。步骤必须连续覆盖 `from+1 .. to`，缺口会在开发期直接报错 */
  readonly to: number;
  /** 给人看的说明，会写进诊断信息 */
  readonly summary: string;
  readonly upgrade: (context: MigrationContext) => void;
}

function createCollection(db: IDBDatabase, name: PersistenceCollection): void {
  if (db.objectStoreNames.contains(name)) return;
  // 键外置（不设 keyPath）：键由调用方给出，见 keys.ts 的键布局
  db.createObjectStore(name);
}

/**
 * 迁移链。`to` 必须从 1 连续排到 {@link PERSISTENCE_SCHEMA_VERSION}。
 *
 * 历史沿革（写在这里，是因为只有这里能解释"为什么库里会有那种形状的记录"）：
 *
 * - v1：建立设置、参数快照、航线草稿、审计日志、元数据五个集合。参数快照是**一条记录内联
 *   全部参数**（`{ model, firmware, params: [...] }`），键为 `机型|固件`。
 * - v2：新增飞前检查记录集合。
 * - v3：参数快照改为「头部 + 分片」，并引入槽位（`last` / `factory-default`）——
 *   在此之前"上次下载的样子"与"原厂默认"只能挤在一个槽里互相覆盖。
 *
 * 迁移**只改写形状真的变了的记录**。审计条目自 v1 起形状没变，因此 v3 不碰它：
 * 改写历史条目既没必要，也违背"审计日志追加式、不许原地修改"这条要求
 * （读取方用 `isReadableFormat` 接受更早格式的记录，见 keys.ts）。
 */
export const PERSISTENCE_MIGRATIONS: readonly MigrationStep[] = [
  {
    to: 1,
    summary: '建立设置 / 参数快照 / 航线草稿 / 审计日志 / 元数据集合',
    upgrade({ db }) {
      createCollection(db, 'settings');
      createCollection(db, 'param-snapshots');
      createCollection(db, 'mission-drafts');
      createCollection(db, 'audit-log');
      createCollection(db, 'meta');
    },
  },
  {
    to: 2,
    summary: '新增飞前检查记录集合',
    upgrade({ db }) {
      createCollection(db, 'preflight-records');
    },
  },
  {
    to: 3,
    summary: '参数快照从「一条记录内联全部参数」拆成「头部 + 分片」，并引入槽位',
    upgrade(context) {
      const { tx } = context;
      const store = tx.objectStore('param-snapshots');
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor === null) return;
        const key = typeof cursor.key === 'string' ? cursor.key : String(cursor.key);
        if (key.startsWith(PARAM_SNAPSHOT_PREFIX)) {
          // 已经是新格式（例如库被新程序写过一半又回退）：不动它
          cursor.continue();
          return;
        }
        migrateLegacySnapshot(context, store, cursor, key);
        cursor.continue();
      };
    },
  },
];

function migrateLegacySnapshot(
  context: MigrationContext,
  store: IDBObjectStore,
  cursor: IDBCursorWithValue,
  key: string,
): void {
  const { report } = context;
  const legacy = normalizeLegacyParamSet(cursor.value);
  if (legacy === null) {
    report({
      collection: 'param-snapshots',
      key,
      reason:
        '旧格式无法解析：记录里缺少 params 数组，或参数条目不是 { name, value }。该记录已原样保留，未做删除。',
    });
    return;
  }

  // 旧键约定是 `机型|固件`。机型与固件本身都不会含 `|`，因此按第一个 `|` 切开是安全的
  const separator = key.indexOf('|');
  if (separator <= 0) {
    report({
      collection: 'param-snapshots',
      key,
      reason: '旧键里找不到「机型|固件」分隔符，无法判断归属，已原样保留。',
    });
    return;
  }
  const model = key.slice(0, separator);
  const firmware = key.slice(separator + 1);

  let built: BuiltParamSnapshot;
  try {
    built = buildParamSnapshot({
      model,
      firmware,
      params: legacy.params,
      slot: 'last',
      source: 'download',
      capturedAt: legacy.capturedAt,
    });
  } catch (error) {
    report({
      collection: 'param-snapshots',
      key,
      reason: `改写为新格式时失败：${error instanceof Error ? error.message : String(error)}。已原样保留。`,
    });
    return;
  }

  store.put(built.header, paramSnapshotChunkKey(model, firmware, 'last', 0));
  for (const chunk of built.chunks) {
    store.put(chunk, paramSnapshotChunkKey(model, firmware, 'last', chunk.index));
  }
  // 旧的单条记录在新布局下已经无用；它没有被"改写成本更低"的替代品，留着只会让读取方困惑
  cursor.delete();
}

/** 读取自述记录；不认识的形状返回 null */
export function readSchemaMeta(value: unknown): SchemaMeta | null {
  if (!isRecordLike(value)) return null;
  const schemaVersion = readFiniteNumber(value['schemaVersion']);
  if (schemaVersion === null) return null;
  const writtenBy = readOptionalString(value['writtenBy']);
  return {
    format: readFiniteNumber(value['format']) ?? 0,
    schemaVersion,
    updatedAt: readFiniteNumber(value['updatedAt']) ?? 0,
    ...(writtenBy === undefined ? {} : { writtenBy }),
  };
}

export function readMigrationIssueReport(value: unknown): MigrationIssueReport | null {
  if (!isRecordLike(value)) return null;
  const raw = value['issues'];
  if (!Array.isArray(raw)) return null;
  const issues: MigrationIssue[] = [];
  for (const entry of raw) {
    if (!isRecordLike(entry)) continue;
    const key = readString(entry['key']);
    const reason = readString(entry['reason']);
    const collection = readString(entry['collection']);
    if (key === null || reason === null || collection === null) continue;
    issues.push({ collection: collection as PersistenceCollection, key, reason });
  }
  return {
    format: readFiniteNumber(value['format']) ?? 0,
    schemaVersion: readFiniteNumber(value['schemaVersion']) ?? 0,
    at: readFiniteNumber(value['at']) ?? 0,
    issues,
  };
}

/** 组装自述记录 */
export function schemaMeta(now: () => number, writtenBy?: string): SchemaMeta {
  return {
    format: RECORD_FORMAT,
    schemaVersion: PERSISTENCE_SCHEMA_VERSION,
    updatedAt: now(),
    ...(writtenBy === undefined ? {} : { writtenBy }),
  };
}

export function migrationIssueReport(
  now: () => number,
  issues: readonly MigrationIssue[],
): MigrationIssueReport {
  return { format: RECORD_FORMAT, schemaVersion: PERSISTENCE_SCHEMA_VERSION, at: now(), issues };
}
